// 编辑器状态：Zustand + immer + zundo(undo/redo)。
// undo/redo 只追踪 sceneId + data（时间/播放状态不入历史栈，对齐原型 pushUndo 只快照 data）。
import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { temporal, TemporalState } from 'zundo'
import type { SceneData, SceneType } from '../api/types'
import { migrateSceneData } from '../scenes/_shared/migrate'

export interface EditorState {
  sceneId: SceneType | null
  data: SceneData
  currentTime: number
  isPlaying: boolean
  duration: number // 秒
  currentProjectId: string | null // 当前编辑项目（null=新建未存）
  currentProjectTitle: string

  // actions
  loadScene: (id: SceneType, data: SceneData, duration: number) => void
  mutateData: (updater: (draft: SceneData) => void) => void
  replaceData: (data: SceneData) => void // 整体替换 data（JSON 导入用；入 undo 栈）
  setCurrentProject: (id: string | null, title?: string) => void
  setTime: (t: number) => void
  setDuration: (d: number) => void
  setPlaying: (p: boolean) => void
  togglePlay: () => void
  resetPlayback: () => void
}

export const useEditorStore = create<EditorState>()(
  temporal(
    immer((set) => ({
      sceneId: null,
      data: {},
      currentTime: 0,
      isPlaying: false,
      duration: 30,
      currentProjectId: null,
      currentProjectTitle: '',

      loadScene: (id, data, duration) =>
        set((s) => {
          s.sceneId = id
          s.data = migrateSceneData(id, data)
          s.duration = duration
          s.currentTime = 0
          s.isPlaying = false
          s.currentProjectId = null
          s.currentProjectTitle = ''
        }),

      mutateData: (updater) =>
        set((s) => {
          updater(s.data)
        }),

      replaceData: (data) =>
        set((s) => {
          s.data = data
        }),

      setCurrentProject: (id, title) =>
        set((s) => {
          s.currentProjectId = id
          s.currentProjectTitle = title ?? ''
        }),

      setTime: (t) =>
        set((s) => {
          s.currentTime = t
        }),

      setDuration: (d) =>
        set((s) => {
          s.duration = Math.max(1, d)
          if (s.currentTime > s.duration) s.currentTime = s.duration
        }),

      setPlaying: (p) =>
        set((s) => {
          s.isPlaying = p
        }),

      togglePlay: () =>
        set((s) => {
          s.isPlaying = !s.isPlaying
        }),

      resetPlayback: () =>
        set((s) => {
          s.currentTime = 0
          s.isPlaying = false
        }),
    })),
    {
      // 仅 sceneId+data 进历史；连续的时间/播放变更不产生 undo 记录
      partialize: (s) => ({ sceneId: s.sceneId, data: s.data }),
      limit: 50,
      equality: (a, b) => a.sceneId === b.sceneId && a.data === b.data,
    },
  ),
)

// undo/redo 便捷入口（zundo 的 temporal 命名空间）
export const editorHistory = () => useEditorStore.temporal.getState()

export function undo() {
  const h = editorHistory()
  if (h.pastStates.length === 0) return false
  h.undo()
  return true
}

export function redo() {
  const h = editorHistory()
  if (h.futureStates.length === 0) return false
  h.redo()
  return true
}

export function clearHistory() {
  useEditorStore.temporal.getState().clear()
}

export type EditorStore = typeof useEditorStore
export type { TemporalState }
